/**
 * Coups de pouce — Attendu 639 « Savoir que le périmètre du disque est
 * proportionnel à son diamètre ».
 *
 * Une entrée par `generator_key` (cf. gen_639.js). Discipline validée en revue
 * le 2026-07-31 : AUCUNE valeur numérique dans l'aide — « 3,14 » est le texte
 * même de la bonne réponse du QCM et le tableau de l'exercice l'affiche déjà.
 * L'aide dit OÙ regarder (la colonne P ÷ D) et QUOI comprendre (même nombre,
 * mesures imparfaites), jamais ce qu'on va y trouver.
 * Justification calibrée 6e : l'imprécision de MESURE (attendu « constater
 * expérimentalement ») — l'irrationalité de π est une notion de 3e, bannie de
 * ce gen par arbitrage du 2026-07-28.
 */

// ── Schéma : deux disques de tailles très différentes, même rapport ──
// Couleurs EXPLICITES (jamais de currentColor — thème sombre élève).
// Cercles en orange #fb923c (convention « partie cercle » du domaine),
// diamètres en pointillés gris #64748b, texte sombre #0f172a.
const SVG_MEME_RAPPORT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 150" width="210" height="93" role="img"
     aria-label="Un petit disque et un grand disque : périmètre divisé par diamètre donne le même nombre pour les deux">
  <circle cx="60" cy="62" r="22" fill="none" stroke="#fb923c" stroke-width="3"/>
  <line x1="38" y1="62" x2="82" y2="62" stroke="#64748b" stroke-width="2" stroke-dasharray="4,3"/>
  <circle cx="250" cy="62" r="50" fill="none" stroke="#fb923c" stroke-width="3"/>
  <line x1="200" y1="62" x2="300" y2="62" stroke="#64748b" stroke-width="2" stroke-dasharray="4,3"/>
  <text x="155" y="72" text-anchor="middle" font-size="28" font-weight="bold" fill="#0f172a">=</text>
  <text x="60" y="140" text-anchor="middle" font-size="15" fill="#0f172a">P ÷ D</text>
  <text x="250" y="140" text-anchor="middle" font-size="15" fill="#0f172a">P ÷ D</text>
</svg>`.trim();

const CP_639 = {
    // Format saisie : la clé du tirage EST ≈ π (3,13–3,15) → l'aide ne prononce
    // NI « π » NI « 3,14 » (elle servirait la réponse). Elle donne la MÉTHODE :
    // diviser, puis arrondir au centième. Exemple hors domaine (quotient 5,63,
    // très loin de la zone des clés).
    proportionnalite_pd_texte: {
        pages: [
            {
                titre: 'Diviser puis arrondir',
                lignes: [
                    'Calcule **P ÷ D** à la calculatrice : le périmètre divisé par le diamètre.',
                    'Pour **arrondir au centième**, garde **2 chiffres** après la virgule en regardant le **3e** : s\'il vaut 5 ou plus, augmente le dernier chiffre gardé ; sinon, garde-le tel quel.',
                ],
            },
            {
                titre: 'Un exemple',
                lignes: [
                    '22,5 ÷ 4 = **5,625**.',
                    'Dans **5,625**, le 3e chiffre après la virgule est **5**, on augmente : l\'arrondi au centième est **5,63**.',
                ],
            },
        ],
    },

    // Format drag : restitution de cours assumée — l'aide EST la fiche de
    // cours condensée (contrat coup de pouce : XP réduite). Les 12 gabarits
    // tournent autour de ces 4 savoirs ; l'élève choisit lequel s'applique.
    // Pas de figure : elle ne ferait que décorer.
    proportionnalite_pd_texte_trou_drag: {
        pages: [
            {
                titre: 'L\'essentiel du disque',
                lignes: [
                    'La formule : **P = π × D**.',
                    '**π ≈ 3,14** : c\'est une valeur **approchée**, pas exacte.',
                    'Le **diamètre**, c\'est **2 fois** le rayon.',
                    'Si le diamètre est **multiplié** par un nombre, le périmètre est multiplié par **le même nombre**.',
                ],
            },
        ],
    },

    // Format V/F : les lignes SONT des énoncés de cours — toute phrase de cours
    // dans l'aide serait la réponse verbatim d'une ligne. Aide de MÉTHODE
    // uniquement : l'outil de test (la formule), pas les verdicts. D = 10 est
    // hors des pools d'objets (diams 3 à 32, sans 10).
    proportionnalite_pd_tableau_vf: {
        pages: [
            {
                titre: 'Teste chaque phrase',
                lignes: [
                    'Retiens la formule : **P = π × D**. Chaque phrase du tableau peut être **testée** avec elle.',
                    'Prends un diamètre simple, par exemple **D = 10**, et regarde ce que devient P quand D **double** ou **triple**.',
                ],
            },
        ],
    },

    proportionnalite_pd_qcm_unique: {
        pages: [
            {
                titre: 'Le nombre π',
                lignes: [
                    'Dans **tous** les disques, petit ou grand, périmètre ÷ diamètre donne le **même nombre** : il s\'appelle **π**.',
                    'Une **mesure** à la règle n\'est jamais parfaite : les résultats sont **proches** de ce nombre, sans être exactement égaux. **Observe la colonne P ÷ D** du tableau.',
                ],
                svg: SVG_MEME_RAPPORT,
            },
        ],
    },
};

export default CP_639;
